import { opportunityLlmExtractionsService } from '@/services/opportunity-llm-extractions.service';

const initialState = () => ({
  currentJob: null,
  jobResult: null,
  triggerStatus: { error: null, pending: false, resolved: false, rejected: false },
});

const state = initialState();

const actions = {
  async startExtraction(
    { commit, dispatch, rootState },
    {
      opportunityId,
      singleFieldId,
      evaluationGridTemplateId,
      documentIds = null,
      enableTriage = true,
      extractionType = 'fields_extraction',
    },
  ) {
    const isCurrentOpportunity =
      rootState.opportunity.opportunity && rootState.opportunity.opportunity.id === opportunityId;

    if (isCurrentOpportunity) {
      await dispatch('extractionJob/abort', null, { root: true });
      commit('requirementsExtraction/RESET', null, { root: true });
      commit('INIT_JOB', { extractingFieldId: singleFieldId || null, phase: 'queuing' });
      // Seed the header pill right away — the job's session is otherwise only
      // created once the trigger, queue check and job lookup have completed
      // (extractionJob/subscribe then merges the jobId in). The failed-trigger
      // path clears it via extractionJob/_onEnd.
      commit(
        'extractionJob/SET_SESSION',
        {
          opportunityId,
          jobId: null,
          jobType: extractionType === 'requirements_extraction' ? 'requirementsExtraction' : 'fieldsExtraction',
          step: null,
          opportunityName: rootState.opportunity.opportunity.name,
        },
        { root: true },
      );
    }

    commit('TRIGGER');
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      await opportunityLlmExtractionsService.triggerExtraction({
        opportunityId,
        fieldIds: singleFieldId ? [singleFieldId] : null,
        evaluationGridTemplateId,
        documentIds,
        enableTriage,
        extractionType,
        signal,
      });
      commit('TRIGGER_SUCCESS');
    } catch (err) {
      if (signal.aborted || err.name === 'AbortError' || err.name === 'CanceledError') return;
      commit('TRIGGER_ERROR', err);
      if (isCurrentOpportunity) {
        await dispatch(
          'extractionJob/_onEnd',
          { jobType: 'fieldsExtraction', opportunityId, jobId: null, status: 'FAILED', ssePayload: null, error: err },
          { root: true },
        );
      }
      return;
    }
    if (signal.aborted) return;

    if (isCurrentOpportunity) {
      // if evaluation grid changed, refresh the opportunity and its evaluation grid
      if (evaluationGridTemplateId) {
        dispatch('opportunity/getOpportunity', { id: opportunityId, silent: true }, { root: true });
        dispatch('evaluationGrid/getEvaluationGrid', opportunityId, { root: true });
      }
      await dispatch('extractionJob/watchExtractionJob', { opportunityId, controller }, { root: true });
    }
  },

  onQueued({ commit }) {
    commit('INIT_JOB', { phase: 'queuing' });
  },

  onConnecting({ commit }, { jobId }) {
    commit('INIT_JOB', { jobId, phase: 'connecting' });
  },

  onStreaming({ commit }, { jobId }) {
    commit('UPDATE', { jobId, phase: 'streaming' });
  },

  onProgress({ commit }, { jobId, ssePayload }) {
    commit('UPDATE', { jobId, ssePayload, phase: 'streaming' });
  },

  onFieldResult({ commit }, { jobId, fieldResult }) {
    commit('UPDATE', { jobId, fieldResult });
  },

  // The chained requirements extraction job (if the completion payload
  // carries one) is followed by extractionJob/_onEnd, which owns the job
  // orchestration.
  onEnd({ commit, dispatch }, { jobId, status, ssePayload, error, opportunityId }) {
    commit('ON_END', { jobId, status, ssePayload, error });
    if (status === 'COMPLETED') {
      dispatch('opportunity/getOpportunity', { id: opportunityId, silent: true }, { root: true });
      dispatch('evaluationGrid/getEvaluationGrid', opportunityId, { root: true });
    }
  },

  onJobResultAvailable({ commit, dispatch }, { jobResult, opportunityId }) {
    commit('ON_JOB_RESULT_AVAILABLE', { jobResult });
    if (jobResult.status === 'COMPLETED') {
      dispatch('opportunity/getOpportunity', { id: opportunityId, silent: true }, { root: true });
      dispatch('evaluationGrid/getEvaluationGrid', opportunityId, { root: true });
    }
  },
};

const mutations = {
  RESET(state) {
    Object.assign(state, initialState());
  },

  INIT_JOB(state, { extractingFieldId, phase }) {
    state.jobResult = null;
    state.currentJob = {
      jobId: null,
      phase, // 'queuing' | 'connecting' | 'streaming'
      step: null,
      receivedSteps: [],
      currentDocument: null,
      currentProgress: null,
      fieldResults: [],
      extractingFieldId: extractingFieldId || null,
    };
  },

  UPDATE(state, { jobId, ssePayload, phase, fieldResult }) {
    state.jobResult = null;

    const defaultJob = {
      jobId,
      phase: 'streaming',
      step: state.currentJob && state.currentJob.step ? state.currentJob.step : null,
      receivedSteps: state.currentJob && state.currentJob.receivedSteps ? state.currentJob.receivedSteps : [],
      currentDocument: state.currentJob && state.currentJob.currentDocument ? state.currentJob.currentDocument : null,
      currentProgress: state.currentJob && state.currentJob.currentProgress ? state.currentJob.currentProgress : null,
      fieldResults: state.currentJob && state.currentJob.fieldResults ? state.currentJob.fieldResults : [],
    };
    state.currentJob = {
      ...(state.currentJob ? state.currentJob : {}),
      jobId,
      phase: phase || defaultJob.phase,
      currentDocument: ssePayload && ssePayload.document ? ssePayload.document : defaultJob.currentDocument,
      currentProgress: ssePayload && ssePayload.progress != null ? ssePayload.progress : defaultJob.currentProgress,
      step: ssePayload && ssePayload.step ? ssePayload.step : defaultJob.step,
      receivedSteps:
        state.currentJob && state.currentJob.receivedSteps ? state.currentJob.receivedSteps : defaultJob.receivedSteps,
      fieldResults: defaultJob.fieldResults,
    };
    if (ssePayload && ssePayload.step && !state.currentJob.receivedSteps.includes(ssePayload.step)) {
      state.currentJob.receivedSteps.push(ssePayload.step);
    }
    if (fieldResult) {
      state.currentJob.fieldResults.push(fieldResult);
    }
  },

  ON_END(state, { jobId, status, ssePayload, error }) {
    const fieldResults =
      ssePayload && Array.isArray(ssePayload.fieldResults)
        ? ssePayload.fieldResults
        : state.currentJob && state.currentJob.fieldResults && state.currentJob.fieldResults.length > 0
        ? state.currentJob.fieldResults
        : [];
    const documentsUsed =
      ssePayload && ssePayload.meta && Array.isArray(ssePayload.meta.documents_used)
        ? ssePayload.meta.documents_used
        : [];

    state.jobResult = {
      jobId,
      status,
      fieldResults,
      documentsUsed,
      completedAt: Date.now(),
      error: error || null,
    };
    state.currentJob = null;
  },

  ON_JOB_RESULT_AVAILABLE(state, { jobResult }) {
    state.jobResult = {
      jobId: jobResult.id,
      status: jobResult.status,
      fieldResults: [],
      documentsUsed: jobResult.documents_used ? jobResult.documents_used : [],
      completedAt: jobResult.updated_at ? new Date(jobResult.updated_at).getTime() : null,
      error: jobResult.error_message ? jobResult.error_message : null,
    };
    state.currentJob = null;
  },

  TRIGGER(state) {
    state.triggerStatus = { error: null, pending: true, resolved: false, rejected: false };
  },
  TRIGGER_SUCCESS(state) {
    state.triggerStatus = { error: null, pending: false, resolved: true, rejected: false };
  },
  TRIGGER_ERROR(state, error) {
    state.triggerStatus = { error, pending: false, resolved: false, rejected: true };
  },
};

const getters = {
  isExtracting: (state) => !!state.currentJob,
  extractingFieldId: (state) => (state.currentJob ? state.currentJob.extractingFieldId : null),
  isQueued: (state) => !!(state.currentJob && state.currentJob.phase === 'queuing'),
  queuePosition: (state, _getters, _rootState, rootGetters) => {
    if (!state.currentJob || state.currentJob.phase !== 'queuing') return null;
    return rootGetters['opportunityDocumentsQueue/keywordQueuePosition'];
  },
  currentStep: (state) => (state.currentJob ? state.currentJob.step : null),
  documentsUsed: (state) =>
    state.jobResult && Array.isArray(state.jobResult.documentsUsed) ? state.jobResult.documentsUsed : [],
  // The documents the last extraction used that the opportunity still has: the job keeps naming those
  // deleted since, and an extraction asked on them fails in the indexer.
  previousDocumentIds: (_state, getters, _rootState, rootGetters) => {
    const opportunity = rootGetters['opportunity/opportunity'];
    const documentIds = new Set(opportunity && opportunity.documents ? opportunity.documents.map((d) => d.id) : []);
    return getters.documentsUsed.map((d) => d.id).filter((id) => documentIds.has(id));
  },
};

export const fieldsExtraction = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
